<template>
  <Modal 
    :visible="visible" 
    :title="`游戏记录 (${betId || '-'})`"
    maxWidth="5xl"
    @close="$emit('close')"
  >
    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center h-48">
      <div class="loader"></div>
    </div>
    
    <!-- 游戏记录内容 -->
    <div v-else class="p-2">
      <!-- 注单摘要信息 -->
      <div v-if="betSummary.fundsn" class="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 class="text-md font-medium text-gray-800 mb-2">注单信息</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div class="overflow-hidden">
            <p class="text-xs text-gray-500">注单ID</p>
            <p class="text-sm font-medium break-all overflow-ellipsis">{{ betSummary.fundsn }}</p>
          </div>
          <div class="overflow-hidden">
            <p class="text-xs text-gray-500">局号</p>
            <p class="text-sm font-medium break-all overflow-ellipsis">{{ betSummary.roundid || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">投注金额</p>
            <p class="text-sm font-medium">{{ formatAmount(betSummary.bet_amount, betSummary.currency) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">派彩金额</p>
            <p class="text-sm font-medium">{{ formatAmount(betSummary.payout, betSummary.currency) }}</p>
          </div>
        </div>
      </div>

      <div v-if="error" class="text-center py-6">
        <div class="flex flex-col items-center justify-center">
          <svg class="w-12 h-12 text-red-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-base text-gray-500">暂无游戏记录数据或加载失败</p>
        </div>
      </div>

      <!-- 游戏记录表格 -->
      <div v-else-if="records.length > 0" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 table-fixed">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px]">交易ID</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px]">注单ID</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px]">局号</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">用户名</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作类型</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金额</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">游戏代码</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">记录时间</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="record in records" :key="record.id">
              <td class="px-6 py-4 text-sm text-gray-500">{{ record.transaction || '-' }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ record.fundsn || '-' }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ record.roundid || '-' }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ record.username || '-' }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ record.type || record.action_type || '-' }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ formatAmount(record.amount, record.currency) }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ record.game_code || '-' }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ formatDatetime(record.created_at) }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">
                <button type="button" @click="viewRecordDetail(record)" class="text-blue-600 hover:text-blue-800">
                  <i class="fas fa-eye"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="text-center py-6">
        <p class="text-base text-gray-500">暂无游戏记录数据</p>
      </div>
    </div>

    <template #footer>
      <button 
        @click="$emit('close')" 
        type="button" 
        class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
      >
        关闭
      </button>
    </template>
  </Modal>

  <!-- 游戏记录详情模态框 -->
  <Modal 
    v-if="showRecordDetail"
    :visible="showRecordDetail" 
    title="游戏记录详情"
    maxWidth="3xl"
    @close="showRecordDetail = false"
  >
    <div v-if="recordDetailLoading" class="flex justify-center items-center h-48">
      <div class="loader"></div>
    </div>
    <div v-else class="p-4 bg-gray-50 rounded-lg mx-2">
      <pre class="text-sm text-gray-700 overflow-x-auto whitespace-pre-wrap">{{ recordDetailContent }}</pre>
    </div>

    <template #footer>
      <button 
        @click="showRecordDetail = false" 
        type="button" 
        class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
      >
        关闭
      </button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import Modal from '@/components/common/Modal.vue';
import AdminApiClient from '@/utils/admin-api-client';

const apiClient = new AdminApiClient();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  betId: {
    type: [String, Number],
    default: ''
  },
  system: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close']);

// 游戏记录状态
const loading = ref(false);
const error = ref(false);
const records = ref([]);
const betSummary = ref({});

// 游戏记录详情状态
const showRecordDetail = ref(false);
const recordDetailLoading = ref(false);
const recordDetailContent = ref('');
const selectedRecordId = ref('');

// 监听可见性变化
watch(() => props.visible, async (newVal) => {
  if (newVal && props.betId && props.system) {
    await loadGameRecords(props.betId, props.system);
  }
});

// 加载游戏记录
async function loadGameRecords(betId, system) {
  if (!betId || !system) return;
  
  loading.value = true;
  error.value = false;
  records.value = [];
  betSummary.value = {};
  
  try {
    // 使用API客户端获取游戏记录
    const response = await apiClient.getGameRecords(betId, system);
    
    if (response && response.success && response.data) {
      records.value  = response.data;
    } else {
      error.value = true;
      console.error('获取游戏记录失败:', response?.message);
    }
  } catch (e) {
    console.error('获取游戏记录出错:', e);
    error.value = true;
  } finally {
    loading.value = false;
  }
}

// 查看记录详情
async function viewRecordDetail(record) {
  if (!record) return;
  
  selectedRecordId.value = record.id;
  showRecordDetail.value = true;
  recordDetailLoading.value = true;
  recordDetailContent.value = '';
  
  try {
    recordDetailContent.value = JSON.stringify(record.data, null, 2);
  } catch (error) {
    console.error('获取游戏记录详情出错:', error);
    recordDetailContent.value = `加载失败: ${error.message || '未知错误'}`;
  } finally {
    recordDetailLoading.value = false;
  }
}

// 格式化金额显示
function formatAmount(amount, currency) {
  if (amount === null || amount === undefined) return '-';
  
  const numAmount = parseFloat(amount);
  const formatter = new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${formatter.format(numAmount)} ${currency || ''}`.trim();
}

// 格式化日期时间显示
function formatDatetime(datetime) {
  if (!datetime) return '-';
  
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return datetime;
  
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-');
}
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 